import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { API } from "../../config/api";
import { errorMessage } from "../../utils/errorMessage";

/**
 * Brief 14d — Panel C: CCL validator blocked events feed.
 *
 * COLP-only. Manages its own fetch state (independent of the summary).
 * Cursor pagination via the ``before`` parameter; the previous-page
 * cursor is appended on Load more.
 */
export default function CCLBlockedFeed() {
  const [events, setEvents] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const authHeaders = useCallback(() => {
    const email =
      sessionStorage.getItem("bonsai_user_id") ||
      localStorage.getItem("bonsai_user_id") ||
      "";
    return { "X-Bonsai-User-Id": email };
  }, []);

  const fetchPage = useCallback(
    async (before = null, append = false) => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        params.set("limit", "20");
        if (before) params.set("before", before);
        const res = await axios.get(
          `${API}/compliance/dashboard/ccl-blocked?${params.toString()}`,
          { headers: authHeaders() }
        );
        const data = res.data || {};
        const incoming = Array.isArray(data.events) ? data.events : [];
        setEvents((prev) => (append ? [...prev, ...incoming] : incoming));
        setHasMore(!!data.has_more);
        setNextCursor(data.next_cursor || null);
      } catch (err) {
        setError(errorMessage(err, "request failed"));
      } finally {
        setLoading(false);
      }
    },
    [authHeaders]
  );

  useEffect(() => {
    fetchPage(null, false);
  }, [fetchPage]);

  if (loading && events.length === 0) {
    return (
      <section
        data-testid="ccl-blocked-feed"
        className="rounded-xl border border-white/[0.07] bg-navy-card p-4"
      >
        <div data-testid="ccl-blocked-feed-skeleton" className="h-4 w-44 bg-slate-800 rounded animate-pulse" />
      </section>
    );
  }

  if (error) {
    return (
      <section
        data-testid="ccl-blocked-feed"
        className="rounded-md border border-red-800/50 bg-red-950/30 p-4 text-sm text-red-200"
      >
        <span data-testid="ccl-blocked-feed-error">CCL block data unavailable. Try reloading.</span>{" "}
        <button
          type="button"
          onClick={() => fetchPage(null, false)}
          className="ml-2 px-2 py-0.5 rounded border border-red-700/60 hover:bg-red-900/30"
          data-testid="ccl-blocked-feed-retry"
        >
          Retry
        </button>
      </section>
    );
  }

  return (
    <section
      data-testid="ccl-blocked-feed"
      className="rounded-xl border border-white/[0.07] bg-navy-card p-4 space-y-3"
    >
      <h2 className="text-sm font-semibold text-slate-100">CCL Blocked Events</h2>

      {events.length === 0 ? (
        <div className="text-sm text-slate-400" data-testid="ccl-blocked-feed-empty">
          No CCL confirmation blocks recorded.
        </div>
      ) : (
        <table className="w-full text-sm text-slate-200" data-testid="ccl-blocked-feed-table">
          <thead>
            <tr className="text-xs uppercase tracking-wider text-slate-400">
              <th className="text-left py-1 pr-3">Matter Ref</th>
              <th className="text-left py-1 pr-3">Submission</th>
              <th className="text-left py-1 pr-3">Blocked At</th>
              <th className="text-left py-1 pr-3">Reason</th>
              <th className="text-right py-1 pr-3">Missing sections</th>
              <th className="text-right py-1">SRA string missing</th>
            </tr>
          </thead>
          <tbody>
            {events.map((evt) => (
              <tr
                key={evt.id || `${evt.matter_ref}-${evt.blocked_at}`}
                data-testid="ccl-blocked-feed-row"
                className="border-t border-slate-800/60"
              >
                <td className="py-1 pr-3">{evt.matter_ref || "—"}</td>
                <td className="py-1 pr-3 font-mono text-xs">{evt.submission_id || "—"}</td>
                <td className="py-1 pr-3" title={evt.blocked_at}>
                  {formatRelative(evt.blocked_at)}
                </td>
                <td className="py-1 pr-3">{evt.block_reason || "—"}</td>
                <td className="py-1 pr-3 text-right">{evt.missing_sections_count ?? 0}</td>
                <td className="py-1 text-right">{evt.sra_string_missing ? "yes" : "no"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {hasMore && (
        <button
          type="button"
          onClick={() => fetchPage(nextCursor, true)}
          disabled={loading}
          className="px-3 py-1.5 rounded-md text-xs font-medium bg-slate-800 text-slate-200 border border-slate-700/50 hover:bg-slate-700 disabled:opacity-50"
          data-testid="ccl-blocked-feed-load-more"
        >
          {loading ? "Loading…" : "Load more"}
        </button>
      )}
    </section>
  );
}

function formatRelative(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const diffSec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diffSec < 60) return "just now";
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  return `${Math.floor(diffSec / 86400)}d ago`;
}
